import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StateCityService } from '@shared/providers/state-city.service';
import { UtilsService } from '@shared/providers/utils.service';
import { CEP, ZipcodeSearchService } from '@shared/providers/zipcode-search.service';
import { IonicSelectableComponent } from 'ionic-selectable';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
})
export class AddressFormComponent implements OnInit, OnDestroy {
  @Input() showButton = true;
  @Input() addressInformations: any;
  @Input() showReference: boolean;

  public addressForm: FormGroup;

  public statesList = [];
  public citiesList = [];
  public allCities = [];

  public citiesPerPage = 20;
  public citiesPage = 1;

  public editMode = false;

  private zipCodeSubscription = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private zipcodeSearchService: ZipcodeSearchService,
    private stateCityService: StateCityService,
    private utils: UtilsService
  ) { }

  get zipCode() {
    return this.addressForm.get('zipCode');
  }
  get street() {
    return this.addressForm.get('street');
  }
  get streetNumber() {
    return this.addressForm.get('streetNumber');
  }
  get complement() {
    return this.addressForm.get('complement');
  }
  get neighborhood() {
    return this.addressForm.get('neighborhood');
  }
  get state() {
    return this.addressForm.get('state');
  }
  get city() {
    return this.addressForm.get('city');
  }
  get reference() {
    return this.addressForm.get('reference');
  }

  ngOnInit() {
    this.addressForm = this.initializeForm();
    this.statesList = this.stateCityService.getStates();

    if (this.addressInformations) {
      this.addressForm.patchValue(this.addressInformations);
      if (this.addressInformations?.zipCode) {
        this.zipCode.setValue(
          this.addressInformations.zipCode.replace(/^(\d{2})(\d{3})(\d{3})$/, '$1.$2-$3')
        );
      }
      this.findStateCity();
    }
  }

  ngAfterViewInit() {
    // Listen to changes in zip code to update city and state
    this.zipCodeSubscription = this.zipCode.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(800))
      .subscribe(async zipCode => {
        if (zipCode !== '' && zipCode.length >= 9) {
          try {
            await this.utils.presentLoading();
            const address = await this.zipcodeSearchService.searchStateAndCityByZipCode(zipCode);
            const index = this.statesList.findIndex(a => a.sigla === address.uf);
            if (index !== -1) {
              if (!(this.addressInformations && address.cep === this.addressInformations.zipCode)) {
                this.addressForm.patchValue({
                  street: address.logradouro,
                  neighborhood: address.bairro,
                  complement: address.complemento,
                });
              }
              this.state.setValue(this.statesList[index]);
              this.stateSelected({ value: this.statesList[index] });
              const i = this.allCities.findIndex(
                a => a.normalized === this.utils.normalizeString(address.localidade)
              );
              this.city.setValue(this.allCities[i]);
            }
          } catch (e) {
            console.error(e);
          } finally {
            if (zipCode !== '') {
              this.utils.dismissLoading();
            }
          }
        }
      });
  }

  ngOnDestroy() {
    this.zipCodeSubscription.unsubscribe();
  }

  initializeForm(): FormGroup {
    return this.formBuilder.group({
      zipCode: ['', Validators.compose([Validators.required, Validators.maxLength(11)])],
      street: ['', Validators.compose([Validators.required, Validators.maxLength(60)])],
      streetNumber: ['', Validators.compose([Validators.required, Validators.maxLength(10)])],
      complement: ['', Validators.maxLength(60)],
      neighborhood: ['', Validators.compose([Validators.required, Validators.maxLength(50)])],
      state: ['', Validators.required],
      city: [{ value: '', disabled: true }, Validators.required],
      reference: ['', Validators.maxLength(100)],
    });
  }

  async findStateCity() {
    const address: CEP = await this.zipcodeSearchService.searchStateAndCityByZipCode(
      this.addressInformations.zipCode
    );
    const index = this.statesList.findIndex(a => a.sigla === address.uf);

    this.state.setValue(this.statesList[index]);
    this.stateSelected({ value: this.statesList[index] });
    const i = this.allCities.findIndex(a => a.normalized === this.utils.normalizeString(address.localidade));
    this.city.setValue(this.allCities[i]);
  }

  stateSelected(state: any) {
    if (state?.value?.sigla) {
      this.handleStateChange(state.value.sigla);
      this.city.enable();
      this.city.setValue('');
      this.citiesPage = 1;
    }
  }

  handleStateChange(selectedStateAcronym: string) {
    const selectedState = this.stateCityService.getStatesByAcronym(selectedStateAcronym);
    if (selectedState) {
      this.allCities = this.stateCityService.getCitiesFromState(selectedState.id);
      this.citiesList = this.allCities.slice(0, this.citiesPerPage);
    }
  }

  getMoreCities(event: { component: IonicSelectableComponent; text: string }) {
    if (this.citiesPerPage * this.citiesPage > this.allCities.length) {
      event.component.disableInfiniteScroll();
      return;
    }

    this.citiesList = this.citiesList.concat(
      this.allCities.slice(this.citiesPerPage * this.citiesPage++, this.citiesPerPage * this.citiesPage)
    );

    event.component.endInfiniteScroll();
  }

  searchCities(event: { component: IonicSelectableComponent; text: string }) {
    if (event.text) {
      event.component.startSearch();

      const cities = this.allCities.filter(city => {
        return city.normalized.includes(this.utils.normalizeString(event.text));
      });

      event.component.items = cities;
    } else {
      event.component.items = this.citiesList;
    }
    event.component.endSearch();
  }

  searchState(event: { component: IonicSelectableComponent; text: string }) {
    if (event.text) {
      event.component.startSearch();
      const states = this.statesList.filter(state => {
        return state.normalized.includes(this.utils.normalizeString(event.text));
      });

      event.component.items = states;
    } else {
      event.component.items = this.statesList;
    }
    event.component.endSearch();
  }
}
